import { ArrowLeft, Maximize, Minimize } from 'lucide-react';
import { memo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../common/Logo';
import { CompactOfflineButton } from '../offline/CompactOfflineButton';
import { TTSToggleButton } from './TTSToggleButton';
import { ttsService } from '../../services/ttsService';
import styles from './ReaderHeader.module.css';
import { useAppName, useAppIconUrl } from '../../hooks/useTenantBranding';
import { useTranslation } from '../../i18n/useTranslation';
import { LanguagePicker } from '../../i18n/LanguagePicker';

interface ReaderHeaderProps {
  bookId: string;
  title: string;
  author?: string;
  isFullScreen: boolean;
  onToggleFullScreen: () => void;
  // Optional current location for TTS
  currentChapterIndex?: number;
  currentPageNumber?: number;
}

/**
 * ReaderHeader - Minimal Header
 *
 * Ultra-minimal header bar for maximum reading space:
 * - Only Back button, Book Title, and Fullscreen toggle
 * - Logo moved to footer
 * - Settings and Download moved to floating dock
 * - WCAG 2.2 AA compliance (44px touch targets)
 * - Auto-hide capability on scroll
 */
function ReaderHeader({
  bookId,
  title,
  author,
  isFullScreen,
  onToggleFullScreen,
  currentChapterIndex,
  currentPageNumber,
}: ReaderHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const appName = useAppName();
  const appIconUrl = useAppIconUrl();
  const [bookPageTextId, setBookPageTextId] = useState<string | null>(null);
  const [isLoadingPageText, setIsLoadingPageText] = useState(false);

  // Fetch BookPageText ID when location changes
  useEffect(() => {
    const fetchBookPageTextId = async () => {
      // Only fetch if we have a location
      if (!bookId || (currentChapterIndex === undefined && currentPageNumber === undefined)) {
        setBookPageTextId(null);
        return;
      }

      setIsLoadingPageText(true);
      try {
        const id = await ttsService.getBookPageTextId(
          bookId,
          currentChapterIndex,
          currentPageNumber
        );
        setBookPageTextId(id);
      } catch (error) {
        console.error('Failed to fetch BookPageText ID:', error);
        setBookPageTextId(null);
      } finally {
        setIsLoadingPageText(false);
      }
    };

    fetchBookPageTextId();
  }, [bookId, currentChapterIndex, currentPageNumber]);

  return (
    <header className={`${styles.header} ${isFullScreen ? styles.fullscreen : ''}`}>
      {/* Left Section: Logo (mobile) + Back Button */}
      <div className={styles.leftSection}>
        {/* Logo - visible on mobile only */}
        <div className={styles.logoContainer}>
          <Logo size="xs" variant="icon" alt={appName} customIconUrl={appIconUrl} />
        </div>

        <button
          onClick={() => navigate('/')}
          className={styles.backButton}
          aria-label={t('App.Reader.BackToLibrary')}
          title={t('App.Reader.BackToLibrary')}
        >
          <ArrowLeft className={styles.backButtonIcon} />
          <span className={styles.backButtonText}>{t('App.Reader.Back')}</span>
        </button>
      </div>

      {/* Center Section: Book Title & Author */}
      <div className={styles.centerSection}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>{title}</h1>
          {author && (
            <p className={styles.author}>{author}</p>
          )}
        </div>
      </div>

      {/* Right Section: TTS + Offline + Fullscreen */}
      <div className={styles.rightSection}>
        {/* Read Aloud TTS Button - always shown; disabled when no BookPageText for this location */}
        {!isLoadingPageText && (
          <TTSToggleButton
            bookPageTextId={bookPageTextId}
          />
        )}

        {/* Language Picker */}
        <LanguagePicker />

        {/* Offline Download Button */}
        <CompactOfflineButton
          bookId={bookId}
          variant="icon"
          size="sm"
          className={styles.actionButton}
        />

        {/* Fullscreen Toggle */}
        <button
          onClick={onToggleFullScreen}
          className={`${styles.actionButton} ${isFullScreen ? styles.active : ''}`}
          aria-label={isFullScreen ? t('App.Reader.ExitFullscreen') : t('App.Reader.EnterFullscreen')}
          data-tooltip={isFullScreen ? t('App.Reader.ExitFullscreenTooltip') : t('App.Reader.FullscreenTooltip')}
        >
          {isFullScreen ? (
            <Minimize className={styles.actionButtonIcon} />
          ) : (
            <Maximize className={styles.actionButtonIcon} />
          )}
        </button>
      </div>
    </header>
  );
}

export default memo(ReaderHeader);
